import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { getCategories, addCategory, updateCategory, deleteCategory } from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Tags, Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const iconOptions = ['Tag', 'ShoppingCart', 'Home', 'Car', 'Utensils', 'Zap', 'Heart', 'Book', 'Coffee', 'Gift'];
const colorOptions = ['#064E3B', '#9A3412', '#1E40AF', '#7C2D12', '#4C1D95', '#0F766E', '#BE123C', '#065F46'];

export const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [saving, setSaving] = useState({ add: false, edit: false, delete: false });
  
  const [formData, setFormData] = useState({
    name: '',
    icon: 'Tag',
    color: '#064E3B'
  });

  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, category: null, reason: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (saving.add) return;
    setSaving((state) => ({ ...state, add: true }));
    try {
      await addCategory(formData);
      toast.success('Category created successfully');
      setIsAddOpen(false);
      setFormData({ name: '', icon: 'Tag', color: '#064E3B' });
      fetchCategories();
    } catch (error) {
      toast.error(error.message || 'Failed to create category');
    } finally {
      setSaving((state) => ({ ...state, add: false }));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (saving.edit) return;
    setSaving((state) => ({ ...state, edit: true }));
    try {
      await updateCategory(currentCategory.id, formData);
      toast.success('Category updated successfully');
      setIsEditOpen(false);
      setCurrentCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.message || 'Failed to update category');
    } finally {
      setSaving((state) => ({ ...state, edit: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.reason?.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    if (saving.delete) return;
    setSaving((state) => ({ ...state, delete: true }));
    try {
      await deleteCategory(deleteConfirm.category.id, deleteConfirm.reason);
      toast.success('Category deleted successfully');
      setDeleteConfirm({ show: false, category: null, reason: '' });
      fetchCategories();
    } catch (error) {
      toast.error(error.message || 'Failed to delete category');
    } finally {
      setSaving((state) => ({ ...state, delete: false }));
    }
  };

  const openDeleteConfirm = (category) => {
    setDeleteConfirm({ show: true, category, reason: '' });
  };

  const openEdit = (category) => {
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon,
      color: category.color
    });
    setIsEditOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-600">Loading categories...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Categories</h1>
            <p className="text-stone-600">Organize your expenses by category</p>
          </div>
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="add-category-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-category-dialog">
              <DialogHeader>
                <DialogTitle>Add New Category</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <Label>Category Name</Label>
                  <Input
                    data-testid="category-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Food, Transport, Bills"
                    className="bg-stone-50"
                    required
                  />
                </div>
                <div>
                  <Label>Icon</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {iconOptions.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.icon === icon ? 'border-emerald-900 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <Tags className="w-5 h-5 mx-auto" />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`h-12 rounded-lg border-2 transition-all ${
                          formData.color === color ? 'border-stone-900 scale-110' : 'border-stone-200'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={saving.add} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="category-submit-button">
                  {saving.add ? 'Saving...' : 'Create Category'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>

        {categories.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-stone-200 rounded-xl p-12 text-center"
          >
            <Tags className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No categories yet</h3>
            <p className="text-stone-600 mb-6">Create categories to organize your expenses</p>
            <Button onClick={() => setIsAddOpen(true)} className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="create-first-category-cta">
              <Plus className="w-4 h-4 mr-2" />
              Create Category
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-stone-200 rounded-xl p-6 hover:shadow-lg transition-all"
                data-testid={`category-card-${category.id}`}
              >
                <div className="flex items-center mb-4">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mr-3"
                    style={{ backgroundColor: category.color + '20' }}
                  >
                    <Tags className="w-6 h-6" style={{ color: category.color }} strokeWidth={1.5} />
                  </div>
                  <p className="text-lg font-bold text-stone-900">{category.name}</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => openEdit(category)}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full"
                    data-testid={`edit-category-${category.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => openDeleteConfirm(category)}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full text-orange-800 hover:bg-orange-50"
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent data-testid="edit-category-dialog">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label>Category Name</Label>
              <Input
                data-testid="edit-category-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-stone-50"
                required
              />
            </div>
            <div>
              <Label>Icon</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {iconOptions.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.icon === icon ? 'border-emerald-900 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <Tags className="w-5 h-5 mx-auto" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Color</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`h-12 rounded-lg border-2 transition-all ${
                      formData.color === color ? 'border-stone-900 scale-110' : 'border-stone-200'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <Button type="submit" disabled={saving.edit} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="edit-category-submit-button">
              {saving.edit ? 'Saving...' : 'Update Category'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, category: null, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-stone-600">
              Are you sure you want to delete this category? This action cannot be undone.
            </p>
            {deleteConfirm.category && (
              <div className="bg-stone-50 rounded-lg p-3 text-sm flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: deleteConfirm.category.color + '20' }}
                >
                  <Tags className="w-5 h-5" style={{ color: deleteConfirm.category.color }} />
                </div>
                <p className="font-medium">{deleteConfirm.category.name}</p>
              </div>
            )}
            <div>
              <Label className="text-stone-700">Reason for Deletion *</Label>
              <Input
                data-testid="delete-category-reason-input"
                value={deleteConfirm.reason}
                onChange={(e) => setDeleteConfirm({ ...deleteConfirm, reason: e.target.value })}
                placeholder="Why are you deleting this category?"
                className="mt-1"
                required
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ show: false, category: null, reason: '' })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={saving.delete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-category-btn"
              >
                {saving.delete ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
